import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Mail, Phone, Send, Loader2, CheckCircle2, MailCheck, PenSquare } from "lucide-react";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

export default function Admin() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);       // lead being replied to
  const [draft, setDraft] = useState({ subject: "", body: "", to: "", reply_to: "" });
  const [drafting, setDrafting] = useState(false);
  const [sending, setSending] = useState(false);
  const [composeOpen, setComposeOpen] = useState(false);
  const [compose, setCompose] = useState({ to: "", subject: "", body: "" });
  const [composeSending, setComposeSending] = useState(false);
  const composeEmailValid = EMAIL_RE.test(compose.to);

  const loadLeads = () => api.get("/leads").then((r) => setLeads(r.data)).catch(() => {}).finally(() => setLoading(false));
  useEffect(() => { loadLeads(); }, []);

  const openReply = async (lead) => {
    setActive(lead);
    setDrafting(true);
    setDraft({ subject: "", body: "", to: lead.email, reply_to: "" });
    try {
      const { data } = await api.get(`/leads/${lead.id}/ack`);
      setDraft({ subject: data.subject, body: data.body, to: data.to, reply_to: data.reply_to });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not build draft.");
      setActive(null);
    } finally {
      setDrafting(false);
    }
  };

  const sendAck = async () => {
    if (!active) return;
    setSending(true);
    try {
      const { data } = await api.post(`/leads/${active.id}/ack`, { subject: draft.subject, body: draft.body });
      toast.success(data.message || "Acknowledgement sent.");
      setActive(null);
      loadLeads();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not send email.");
    } finally {
      setSending(false);
    }
  };

  const sendCompose = async () => {
    if (!composeEmailValid) { toast.error("Enter a valid email address."); return; }
    setComposeSending(true);
    try {
      const { data } = await api.post("/admin/send-email", compose);
      toast.success(data.message || "Email sent.");
      setComposeOpen(false);
      setCompose({ to: "", subject: "", body: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not send email.");
    } finally {
      setComposeSending(false);
    }
  };

  return (
    <div className="bg-cream min-h-screen pt-[72px]" data-testid="admin-page">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 pt-14 lg:pt-20 pb-28">
        <span className="overline text-cobalt">Admin</span>
        <div className="flex flex-wrap items-end justify-between gap-4 mt-4">
          <div>
            <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight text-navy">Incoming leads</h1>
            <p className="text-slateink mt-2">{loading ? "Loading…" : `${leads.length} enquiries captured`}</p>
          </div>
          <button onClick={() => setComposeOpen(true)} data-testid="admin-compose-button"
            className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-navy text-white font-medium hover:bg-cobalt transition-colors">
            <PenSquare size={16} /> Compose email
          </button>
        </div>
        <div className="mb-10" />

        {!loading && leads.length === 0 ? (
          <div className="bg-white border border-border p-10 text-center text-slateink" data-testid="admin-empty">No leads yet.</div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="admin-leads">
            {leads.map((l) => (
              <div key={l.id} className="bg-white border border-border p-6 flex flex-col" data-testid={`lead-${l.id}`}>
                <div className="flex items-center justify-between gap-3">
                  <p className="font-display text-xl font-semibold text-navy">{l.name}</p>
                  <span className={`font-mono-ui text-[0.55rem] uppercase tracking-widest px-2 py-1 ${l.status === "acknowledged" ? "bg-cobalt text-white" : "bg-secondary text-navy"}`}>{l.status}</span>
                </div>
                <span className="inline-block mt-2 w-fit font-mono-ui text-[0.6rem] uppercase tracking-widest text-cobalt border border-cobalt/30 px-2 py-1">{l.service}</span>
                <div className="mt-4 space-y-2 text-sm text-slateink">
                  <p className="flex items-center gap-2"><Mail size={14} />{l.email}</p>
                  {l.phone && <p className="flex items-center gap-2"><Phone size={14} />{l.phone}</p>}
                </div>
                {l.message && <p className="text-sm text-navy/80 mt-4 leading-relaxed border-t border-border pt-4">{l.message}</p>}
                <p className="text-xs text-slateink/60 mt-4">{new Date(l.created_at).toLocaleString()}</p>

                <div className="mt-4 pt-4 border-t border-border flex items-center justify-between gap-2">
                  {l.status === "acknowledged" && (
                    <span className="flex items-center gap-1.5 text-xs text-cobalt font-medium" data-testid={`lead-ack-badge-${l.id}`}>
                      <CheckCircle2 size={14} /> Acknowledged
                    </span>
                  )}
                  <button onClick={() => openReply(l)} data-testid={`lead-reply-${l.id}`}
                    className="ml-auto inline-flex items-center gap-2 text-sm font-medium px-4 py-2 rounded-full bg-navy text-white hover:bg-cobalt transition-colors">
                    <MailCheck size={15} /> {l.status === "acknowledged" ? "Send again" : "Review & reply"}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Review & send acknowledgement dialog */}
      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent className="max-w-xl" data-testid="ack-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl text-navy">Acknowledgement email</DialogTitle>
            <DialogDescription>Auto-drafted from the chosen package. Review and edit before sending to the user.</DialogDescription>
          </DialogHeader>

          {drafting ? (
            <div className="py-16 grid place-items-center text-slateink"><Loader2 className="animate-spin" /></div>
          ) : (
            <div className="space-y-4">
              <div className="flex flex-wrap gap-3 text-sm">
                <span className="text-slateink">To: <span className="text-navy font-medium" data-testid="ack-to">{draft.to}</span></span>
                <span className="text-slateink">· Replies go to: <span className="text-navy font-medium">{draft.reply_to}</span></span>
                {active?.service && <span className="font-mono-ui text-[0.6rem] uppercase tracking-widest bg-secondary text-navy px-2 py-1">{active.service}</span>}
              </div>

              <label className="block">
                <span className="overline text-slateink block mb-1.5">Subject</span>
                <input data-testid="ack-subject" value={draft.subject} onChange={(e) => setDraft({ ...draft, subject: e.target.value })}
                  className="w-full border border-border px-3 py-2.5 outline-none focus:border-cobalt text-navy" />
              </label>

              <label className="block">
                <span className="overline text-slateink block mb-1.5">Message (auto-drafted — edit before sending)</span>
                <textarea data-testid="ack-body" rows={11} value={draft.body} onChange={(e) => setDraft({ ...draft, body: e.target.value })}
                  className="w-full border border-border px-3 py-2.5 outline-none focus:border-cobalt text-navy resize-none text-sm leading-relaxed" />
              </label>

              <p className="text-xs text-slateink/70">This draft was generated from the chosen package. Review and edit it, then send.</p>
            </div>
          )}

          <DialogFooter className="gap-2">
            <button onClick={() => setActive(null)} data-testid="ack-cancel"
              className="px-5 py-2.5 rounded-full border border-border text-navy hover:bg-secondary transition-colors">Cancel</button>
            <button onClick={sendAck} disabled={sending || drafting || !draft.subject || !draft.body} data-testid="ack-send"
              className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-navy text-white font-medium hover:bg-cobalt transition-colors disabled:opacity-50">
              {sending ? <><Loader2 size={16} className="animate-spin" /> Sending…</> : <><Send size={16} /> Send to user</>}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Admin-only compose email dialog */}
      <Dialog open={composeOpen} onOpenChange={setComposeOpen}>
        <DialogContent className="max-w-xl" data-testid="compose-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl text-navy">Compose email</DialogTitle>
            <DialogDescription>Send a branded email to any recipient. Replies route to your team inbox.</DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <label className="block">
              <span className="overline text-slateink block mb-1.5">To</span>
              <input data-testid="compose-to" type="email" value={compose.to}
                onChange={(e) => setCompose({ ...compose, to: e.target.value })}
                placeholder="name@email.com"
                className={`w-full border px-3 py-2.5 outline-none text-navy ${compose.to && !composeEmailValid ? "border-destructive focus:border-destructive" : "border-border focus:border-cobalt"}`} />
              {compose.to && !composeEmailValid && (
                <span className="text-destructive text-xs mt-1 block" data-testid="compose-email-error">Enter a valid email address.</span>
              )}
            </label>
            <label className="block">
              <span className="overline text-slateink block mb-1.5">Subject</span>
              <input data-testid="compose-subject" value={compose.subject}
                onChange={(e) => setCompose({ ...compose, subject: e.target.value })}
                className="w-full border border-border px-3 py-2.5 outline-none focus:border-cobalt text-navy" />
            </label>
            <label className="block">
              <span className="overline text-slateink block mb-1.5">Message</span>
              <textarea data-testid="compose-body" rows={9} value={compose.body}
                onChange={(e) => setCompose({ ...compose, body: e.target.value })}
                className="w-full border border-border px-3 py-2.5 outline-none focus:border-cobalt text-navy resize-none text-sm leading-relaxed" />
            </label>
          </div>

          <DialogFooter className="gap-2">
            <button onClick={() => setComposeOpen(false)} data-testid="compose-cancel"
              className="px-5 py-2.5 rounded-full border border-border text-navy hover:bg-secondary transition-colors">Cancel</button>
            <button onClick={sendCompose} data-testid="compose-send"
              disabled={composeSending || !composeEmailValid || !compose.subject || !compose.body}
              className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-navy text-white font-medium hover:bg-cobalt transition-colors disabled:opacity-50">
              {composeSending ? <><Loader2 size={16} className="animate-spin" /> Sending…</> : <><Send size={16} /> Send email</>}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
